import { useEffect, useMemo, useRef, useState } from "react";
import ApiUsersManagement from "../../../api/user-manager";
import { useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { selectUsers, appendUsers, resetUsers } from "../../../store/slices/user_management";

const DEFAULT_LIMIT = 30;

const useUsers = ({ search, filters, sort, scope, status, projectId }) => {
  const { pathname } = useLocation();
  const dispatch = useDispatch();
  const users = useSelector(selectUsers) || [];

  const usersRef = useRef(users);
  useEffect(() => { usersRef.current = users; }, [users]);

  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setLoading] = useState(false);
  const sortBy = sort?.field || undefined;
  const sortOrder = sort?.order || undefined; // 'ASC' | 'DESC'

const paramsMemo = useMemo(() => {
  const effectiveStatus =
    filters?.status && filters.status.length ? filters.status : status;

  const { status: _ignoredStatus, ...restFilters } = filters || {};

  return {
    ...restFilters,
    search,
    sortBy,
    sortOrder,
    scope,
    projectId,
    status: effectiveStatus,
  };
}, [filters, search, sortBy, sortOrder, scope, projectId, status]);

  const mergeUnique = (prev, incoming) => {
    const seen = new Set(prev.map(u => u.id));
    return prev.concat((incoming || []).filter(u => !seen.has(u.id)));
  };

  const loadPage = async (nextPage, { replace = false } = {}) => {
    if (isLoading) return;
    setLoading(true);
    try {
      const r = await ApiUsersManagement.getUsers({ ...paramsMemo, page: nextPage, limit: DEFAULT_LIMIT });
      const incoming = r?.data?.users || [];
      const backendHasMore = r?.data?.hasMore;
      const computedHasMore = typeof backendHasMore === "boolean"
        ? backendHasMore
        : incoming.length === DEFAULT_LIMIT;

      let nextList;
      if (replace) {
        nextList = incoming;
      } else {
        nextList = mergeUnique(usersRef.current, incoming);
      }

      dispatch(appendUsers(nextList));
      setHasMore(computedHasMore);
      setPage(nextPage);
    } catch (e) {
      console.log("User management fetch users error.", e);
    } finally {
      setLoading(false);
    }
  };

  const fetchNext = () => {
    if (!hasMore || isLoading) return;
    return loadPage(page + 1, { replace: false });
  };

  const resetAndFetch = async () => {
    setHasMore(true);
    setPage(0);
    dispatch(resetUsers());
    return loadPage(1, { replace: true });
  };

  useEffect(() => { resetAndFetch(); /* eslint-disable-next-line */ }, [pathname]);

  useEffect(() => { resetAndFetch(); /* eslint-disable-next-line */ }, [paramsMemo]);

  return { users, hasMore, isLoading, fetchNext, resetAndFetch };
};

export default useUsers;